import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeftIcon, 
  PencilIcon, 
  EnvelopeIcon, 
  ChatBubbleLeftRightIcon,
  BookOpenIcon,
  CalendarIcon,
  ClockIcon,
  LinkIcon
} from '@heroicons/react/24/outline';
import staffApi from '../../staffApi';

const StudentDetail = ({ onUpdateStudent }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [student, setStudent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [showWhatsAppModal, setShowWhatsAppModal] = useState(false);
  const [emailData, setEmailData] = useState({ subject: '', message: '' });
  const [whatsAppMessage, setWhatsAppMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [magicLink, setMagicLink] = useState(null);
  const [creatingMagicLink, setCreatingMagicLink] = useState(false);

  useEffect(() => {
    fetchStudentDetails();
  }, [id]);

  const fetchStudentDetails = async () => {
    try {
      setLoading(true);
      const response = await staffApi.getStudentById(id);
      if (response.success) {
        setStudent(response.data);
        // Set magic link if it exists
        if (response.data.magic_link) {
          setMagicLink(response.data.magic_link);
        }
      } else {
        setError('Failed to fetch student details');
      }
    } catch (err) {
      setError('Failed to fetch student details');
      console.error('Error fetching student details:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSendEmail = async () => {
    if (!emailData.subject.trim() || !emailData.message.trim()) {
      return;
    }

    setSending(true);
    try {
      const response = await staffApi.sendEmailToStudent(id, emailData.subject, emailData.message);
      if (response.success) {
        setShowEmailModal(false);
        setEmailData({ subject: '', message: '' });
        // You might want to show a success message here
      }
    } catch (error) {
      console.error('Error sending email:', error);
    } finally {
      setSending(false);
    }
  };

  const handleCreateMagicLink = async () => {
    setCreatingMagicLink(true);
    try {
      const response = await staffApi.createMagicLinkForStudent(id);
      if (response.success) {
        setMagicLink(response.data);
        alert('Magic link created successfully!');
      } else {
        alert('Failed to create magic link');
      }
    } catch (error) {
      console.error('Error creating magic link:', error);
      alert('Failed to create magic link');
    } finally {
      setCreatingMagicLink(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      alert('Magic link copied to clipboard!');
    }).catch(err => {
      console.error('Failed to copy:', err);
    });
  };

  const handleSendWhatsApp = async () => {
    if (!whatsAppMessage.trim()) {
      return;
    }

    setSending(true);
    try {
      const response = await staffApi.sendWhatsAppToStudent(id, whatsAppMessage);
      if (response.success) {
        setShowWhatsAppModal(false);
        setWhatsAppMessage('');
        // You might want to show a success message here
      }
    } catch (error) {
      console.error('Error sending WhatsApp message:', error);
    } finally {
      setSending(false);
    }
  };

  const getStatusBadge = (isActive) => {
    const baseClasses = "inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium";
    if (isActive) {
      return `${baseClasses} bg-green-100 text-green-800`;
    } else {
      return `${baseClasses} bg-red-100 text-red-800`;
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (error || !student) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-md p-4">
        <div className="flex">
          <div className="ml-3">
            <h3 className="text-sm font-medium text-red-800">Error</h3>
            <div className="mt-2 text-sm text-red-700">{error || 'Student not found'}</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center">
          <button
            onClick={() => navigate('/staff/students')}
            className="inline-flex items-center text-sm font-medium text-gray-500 hover:text-gray-700 mr-4"
          >
            <ArrowLeftIcon className="h-4 w-4 mr-1" />
            Back to Students
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{student.name}</h1>
            <p className="text-sm text-gray-500">{student.email}</p>
          </div>
        </div>
        <div className="flex items-center space-x-3">
          <span className={getStatusBadge(student.is_active)}>{student.is_active ? 'Active' : 'Inactive'}</span>
          <button
            onClick={() => navigate(`/staff/students/${id}/edit`)}
            className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            <PencilIcon className="h-4 w-4 mr-1" />
            Edit
          </button>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="bg-white shadow rounded-lg p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Quick Actions</h3>
        <div className="flex space-x-4">
          <button
            onClick={() => setShowEmailModal(true)}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            <EnvelopeIcon className="h-4 w-4 mr-2" />
            Send Email
          </button>
          <button
            onClick={() => setShowWhatsAppModal(true)}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
          >
            <ChatBubbleLeftRightIcon className="h-4 w-4 mr-2" />
            Send WhatsApp
          </button>
          <button
            onClick={() => navigate(`/staff/students/${id}/lessons`)}
            className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            <BookOpenIcon className="h-4 w-4 mr-2" />
            View Lesson Logs
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Basic Information */}
        <div className="bg-white shadow rounded-lg p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Basic Information</h3>
          <dl className="space-y-4">
            <div>
              <dt className="text-sm font-medium text-gray-500">Full Name</dt>
              <dd className="mt-1 text-sm text-gray-900">{student.name}</dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500">Email Address</dt>
              <dd className="mt-1 text-sm text-gray-900">{student.email}</dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500">Phone Number</dt>
              <dd className="mt-1 text-sm text-gray-900">{student.phone_no}</dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500">Gender</dt>
              <dd className="mt-1 text-sm text-gray-900">{student.gender}</dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500">Timezone</dt>
              <dd className="mt-1 text-sm text-gray-900">{student.timezone}</dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500">Subject</dt>
              <dd className="mt-1 text-sm text-gray-900">
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                  {student.subject}
                </span>
              </dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500">Location</dt>
              <dd className="mt-1 text-sm text-gray-900">
                {[student.city, student.state, student.country].filter(Boolean).join(', ') || 'Not specified'}
              </dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500">Assigned Tutor</dt>
              <dd className="mt-1 text-sm text-gray-900">{student.assignedTutor || 'Not assigned'}</dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500">Package</dt>
              <dd className="mt-1 text-sm text-gray-900">{student.package}</dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500">Member Since</dt>
              <dd className="mt-1 text-sm text-gray-900">{new Date(student.createdAt).toLocaleDateString()}</dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500">Last Login</dt>
              <dd className="mt-1 text-sm text-gray-900">
                {student.lastLogin ? new Date(student.lastLogin).toLocaleDateString() : 'Never'}
              </dd>
            </div>
          </dl>
        </div>

        {/* Magic Link */}
        <div className="bg-white shadow rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900">Magic Link</h3>
            {!magicLink && (
              <button
                onClick={handleCreateMagicLink}
                disabled={creatingMagicLink}
                className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
              >
                {creatingMagicLink ? 'Creating...' : 'Create Magic Link'}
              </button>
            )}
          </div>
          {magicLink ? (
            <div className="space-y-3">
              <div>
                <label className="text-sm font-medium text-gray-500">Magic Link URL</label>
                <div className="mt-1 flex items-center space-x-2">
                  <div className="flex-1 bg-gray-50 border border-gray-300 rounded p-2">
                    <code className="text-xs break-all">{magicLink.url}</code>
                  </div>
                  <button
                    onClick={() => copyToClipboard(magicLink.url)}
                    className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                  >
                    <LinkIcon className="h-4 w-4 mr-1" />
                    Copy
                  </button>
                </div>
              </div>
              <div className="flex items-center space-x-4 text-sm">
                <div>
                  <span className="text-gray-500">Status: </span>
                  <span className={`font-medium ${
                    magicLink.is_usable ? 'text-green-600' : 
                    magicLink.is_expired ? 'text-red-600' : 
                    'text-gray-600'
                  }`}>
                    {magicLink.is_usable ? 'Active' : magicLink.is_expired ? 'Expired' : 'Inactive'}
                  </span>
                </div>
                {magicLink.expires_at && (
                  <div>
                    <span className="text-gray-500">Expires: </span>
                    <span className="text-gray-900">{new Date(magicLink.expires_at).toLocaleDateString()}</span>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <p className="text-sm text-gray-500">No magic link created yet. Click "Create Magic Link" to generate one.</p>
          )}
        </div>

        {/* Current Lesson Packs */}
        <div className="bg-white shadow rounded-lg p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Lesson Packs</h3>
          {student.lesson_packs && student.lesson_packs.length > 0 ? (
            <div className="space-y-4">
              {student.lesson_packs.map((pack) => (
                <div key={pack.id} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="text-sm font-medium text-gray-900">{pack.subject}</h4>
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      pack.status === 'ACTIVE' ? 'bg-green-100 text-green-800' :
                      pack.status === 'COMPLETED' ? 'bg-blue-100 text-blue-800' :
                      'bg-red-100 text-red-800'
                    }`}>
                      {pack.status}
                    </span>
                  </div>
                  <div className="mb-2">
                    <div className="flex justify-between text-sm text-gray-600 mb-1">
                      <span>Hours Remaining</span>
                      <span>{pack.hours_remaining} / {pack.no_of_hours}</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div 
                        className="bg-indigo-600 h-2 rounded-full" 
                        style={{ width: `${(pack.hours_remaining / pack.no_of_hours) * 100}%` }}
                      ></div>
                    </div>
                  </div>
                  <div className="flex justify-between text-xs text-gray-500">
                    <span>{pack.currency} {pack.price}</span>
                    <span>Purchased: {pack.purchase_date}</span>
                  </div>
                  {pack.expiry_date && (
                    <div className="text-xs text-gray-500 mt-1">
                      Expires: {pack.expiry_date}
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-gray-500">No lesson packs assigned</p>
          )}
        </div>
      </div>

      {/* Recent Sessions */}
      <div className="bg-white shadow rounded-lg p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Recent Sessions</h3>
        {student.sessions && student.sessions.length > 0 ? (
          <div className="space-y-4">
            {student.sessions.map((session) => (
              <div key={session.id} className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center">
                    <CalendarIcon className="h-4 w-4 text-gray-400 mr-2" />
                    <span className="text-sm font-medium text-gray-900">{session.date}</span>
                  </div>
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                    Completed
                  </span>
                </div>
                <div className="mb-2">
                  <h4 className="text-sm font-medium text-gray-900">{session.subject}</h4>
                  <p className="text-sm text-gray-500">
                    {session.tutor_name ? `Tutor: ${session.tutor_name}` : 'No tutor assigned'}
                  </p>
                </div>
                <div className="flex items-center justify-between text-xs text-gray-500">
                  <span className="flex items-center">
                    <ClockIcon className="h-3 w-3 mr-1" />
                    {session.duration} hours
                  </span>
                  <span>{session.notes || 'No notes'}</span>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-gray-500">No sessions available</p>
        )}
      </div>

      {/* Email Modal */}
      {showEmailModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Send Email to {student.name}</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Subject</label>
                  <input
                    type="text"
                    value={emailData.subject}
                    onChange={(e) => setEmailData(prev => ({ ...prev, subject: e.target.value }))}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                    placeholder="Enter email subject"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Message</label>
                  <textarea
                    value={emailData.message}
                    onChange={(e) => setEmailData(prev => ({ ...prev, message: e.target.value }))}
                    rows={4}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                    placeholder="Enter your message"
                  />
                </div>
              </div>
              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={() => setShowEmailModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSendEmail}
                  disabled={sending || !emailData.subject.trim() || !emailData.message.trim()}
                  className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50"
                >
                  {sending ? 'Sending...' : 'Send Email'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* WhatsApp Modal */}
      {showWhatsAppModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Send WhatsApp to {student.name}</h3>
              <div>
                <label className="block text-sm font-medium text-gray-700">Message</label>
                <textarea
                  value={whatsAppMessage}
                  onChange={(e) => setWhatsAppMessage(e.target.value)}
                  rows={4}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                  placeholder="Enter your WhatsApp message"
                />
              </div>
              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={() => setShowWhatsAppModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSendWhatsApp}
                  disabled={sending || !whatsAppMessage.trim()}
                  className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 disabled:opacity-50"
                >
                  {sending ? 'Sending...' : 'Send WhatsApp'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StudentDetail;
